import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  map,
  of,
  repeat,
  tap,
  throwError,
} from 'rxjs';
import { HIDE_ERROR_FEEDBACK } from 'src/app/core/interceptors/http-error.interceptor';
import {
  DeliverableDto,
  DeliverablesApiService,
} from 'src/app/deliverables-admin/services/deliverables-api.service';
import { DeliverablePropertiesParams } from '../../../shared/models/deliverables/deliverables.interface';
import {
  DeliverableItemsPropertiesModel,
  DeliverablePropertiesModel,
} from '../../../shared/models/deliverables/deliverables.model';
import { ItemsService } from './items.service';
import { PropertyDefinitionService } from './property-definitions.service';

@Injectable({ providedIn: 'root' })
export class DeliverableService {
  readonly #deliverableUrl = '/api/deliverables';
  readonly #http = inject(HttpClient);
  readonly #itemsService = inject(ItemsService);
  readonly #propertyDefService = inject(PropertyDefinitionService);
  readonly #deliverablesApiService = inject(DeliverablesApiService);

  readonly #refetchFullDeliverable$ = new Subject<boolean>();

  readonly #isFullDeliverableLoading$ = new BehaviorSubject<boolean>(false);
  isFullDeliverableLoading$ = this.#isFullDeliverableLoading$.asObservable();

  readonly #isDeliverablePropertiesLoading$ = new BehaviorSubject<boolean>(false);
  isDeliverablePropertiesLoading$ = this.#isDeliverablePropertiesLoading$.asObservable();

  readonly #deliverablesProperties$ = new BehaviorSubject<
    UNDEFINED<DeliverablePropertiesModel<DeliverableDto>>
  >(undefined);
  deliverablesProperties$ = this.#deliverablesProperties$.asObservable().pipe(filter(Boolean));

  readonly #deliverableItemsProperties$ = new BehaviorSubject<
    UNDEFINED<DeliverableItemsPropertiesModel<DeliverableDto>>
  >(undefined);
  deliverableItemsProperties$ = this.#deliverableItemsProperties$.asObservable().pipe(filter(Boolean));

  readonly deliverableFull = signal<DeliverableDto | undefined>(undefined);

  get fullDeliverableLoadingInit$() {
    this.#isFullDeliverableLoading$.next(true);
    return this.isFullDeliverableLoading$;
  }

  refetchFullDeliverable() {
    this.#refetchFullDeliverable$.next(true);
    this.#isFullDeliverableLoading$.next(true);
  }

  refetchDeliverablesProperties() {
    this.refetchFullDeliverable();
    this.#propertyDefService.refetchPropertyViewDef();
  }

  refetchDeliverableItemsProperties() {
    this.refetchFullDeliverable();
    this.#itemsService.refetchItems();
    this.#propertyDefService.refetchPropertyViewDef();
  }

  fullDeliverable(deliverableId: number) {
    this.#isFullDeliverableLoading$.next(true);
    return this.#deliverablesApiService.getDeliverableFull({ deliverableId }).pipe(
      repeat({ delay: () => this.#refetchFullDeliverable$ }),
      tap(deliverable => {
        this.deliverableFull.set(deliverable);
        this.#isFullDeliverableLoading$.next(false);
      }),
      catchError(error => {
        this.#isFullDeliverableLoading$.next(false);
        return throwError(() => error);
      }),
    );
  }

  deliverablesProperties<T extends DeliverableDto>(props: DeliverablePropertiesParams) {
    this.#isDeliverablePropertiesLoading$.next(true);

    const fullDeliverable$ = this.fullDeliverable(props.deliverableId).pipe(map(val => val as T));
    const propertyViewDef$ = this.#propertyDefService.propertyViewDefinitions(props);

    return combineLatest([fullDeliverable$, propertyViewDef$]).pipe(
      map(result => new DeliverablePropertiesModel<T>(result)),
      tap(result => this.#deliverablesProperties$.next(result)),
      tap(() => this.#isDeliverablePropertiesLoading$.next(false)),
      catchError(error => {
        this.#isDeliverablePropertiesLoading$.next(false);
        return throwError(() => error);
      }),
    );
  }

  searchDeliverables(projectId: number, query: string): Observable<Record<string, number>> {
    if (!projectId || !query?.trim()) {
      return of({});
    }

    const context = new HttpContext().set(HIDE_ERROR_FEEDBACK, true);
    return this.#http
      .get<Record<string, number>>(`${this.#deliverableUrl}/${projectId}/search?labelNumber=${query}`, {
        context,
      })
      .pipe(catchError(error => throwError(() => error)));
  }
}
